import React, { useState } from 'react';
import { MessageSquarePlusIcon, PlusIcon, XIcon } from 'lucide-react';
export type ProductNote = {
  id: string;
  product: string;
  note: string;
};

// Free-form, product-specific instructions that the permission toggles cannot
// express — a single collaboration that cannot be discounted, a style that must
// stay off paid social, a SKU with a licensing caveat.
export function ProductNotes({
  notes,
  onChange
}: {
  notes: ProductNote[];
  onChange: (notes: ProductNote[]) => void;
}) {
  const [product, setProduct] = useState('');
  const [note, setNote] = useState('');
  const add = () => {
    const trimmedProduct = product.trim();
    const trimmedNote = note.trim();
    if (!trimmedProduct || !trimmedNote) return;
    onChange([...notes, {
      id: `${Date.now()}-${trimmedProduct}`,
      product: trimmedProduct,
      note: trimmedNote
    }]);
    setProduct('');
    setNote('');
  };
  const remove = (id: string) => onChange(notes.filter(item => item.id !== id));
  return <section className="mt-7 border-t border-ink/10 pt-6" aria-labelledby="product-notes-heading" data-id="element-2350" data-mp="c8kmwxoc3lpk">
      <div className="flex items-center gap-2" data-id="element-2351" data-mp="gyrcp0ul1c9e">
        <MessageSquarePlusIcon className="h-4 w-4 text-[#BB5E40]" aria-hidden="true" data-id="element-2352" data-mp="h0ki6ctglqft" />
        <h2 id="product-notes-heading" className="text-sm font-semibold" data-id="element-2353" data-mp="2bfyer70ne5l">Notes on specific products</h2>
      </div>
      <p className="mt-2 max-w-2xl text-sm leading-relaxed text-ink/60" data-id="element-2354" data-mp="79hi9ec9nhi4">
        Add instructions that apply to individual styles, SKUs, or collaborations — anything the permissions above do not
        cover. Carli reads these before recommending channels, pricing, and creative.
      </p>

      {notes.length > 0 && <ul className="mt-4 space-y-2" data-id="element-2355" data-mp="l2z08upb0m61">
          {notes.map(item => <li key={item.id} className="flex items-start justify-between gap-3 rounded-xl border border-ink/10 bg-cream/40 p-3.5" data-id="element-2356" data-mp="pxuum1i0jlmb">
              <div className="min-w-0" data-id="element-2357" data-mp="3apx0riad6u4q">
                <p className="text-xs font-semibold text-ink" data-id="element-2358" data-mp="3aqzmlbsty6ds">{item.product}</p>
                <p className="mt-1 text-xs leading-relaxed text-ink/65" data-id="element-2359" data-mp="1h7pptf76np3r">{item.note}</p>
              </div>
              <button type="button" onClick={() => remove(item.id)} aria-label={`Remove note on ${item.product}`} className="shrink-0 rounded-full p-1 text-ink/40 transition-colors hover:bg-[#BB5E40]/10 hover:text-[#94452E]" data-id="element-2360" data-mp="1c7uwz63ukpg2">
                <XIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-2361" data-mp="xk9t39z5em73" />
              </button>
            </li>)}
        </ul>}

      <div className="mt-4 grid gap-2 sm:grid-cols-[minmax(0,0.85fr)_minmax(0,1.4fr)_auto]" data-id="element-2362" data-mp="tjpxwby4xsnl">
        <input value={product} onChange={event => setProduct(event.target.value)} placeholder="Style name, SKU, or category" aria-label="Product this note applies to" className="rounded-xl border border-ink/15 bg-cream/40 px-3.5 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:border-[#BB5E40] focus:outline-none focus:ring-2 focus:ring-[#BB5E40]/20" data-id="element-2363" data-mp="oisg40h1f0mm" />
        <input value={note} onChange={event => setNote(event.target.value)} onKeyDown={event => {
        if (event.key === 'Enter') {
          event.preventDefault();
          add();
        }
      }} placeholder="What Carli should know or do differently" aria-label="Note" className="rounded-xl border border-ink/15 bg-cream/40 px-3.5 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:border-[#BB5E40] focus:outline-none focus:ring-2 focus:ring-[#BB5E40]/20" data-id="element-2364" data-mp="2lf55dzs9eo97" />
        <button type="button" onClick={add} disabled={!product.trim() || !note.trim()} className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-ink/15 bg-paper px-4 py-2.5 text-sm font-semibold text-ink transition-colors hover:border-[#BB5E40]/40 hover:text-[#94452E] disabled:cursor-not-allowed disabled:opacity-45" data-id="element-2365" data-mp="2cfi6plqwzwju">
          <PlusIcon className="h-4 w-4" aria-hidden="true" data-id="element-2366" data-mp="3qgnyabqxldo3" />
          Add note
        </button>
      </div>
    </section>;
}